// ================================================================================
// Custom events
//
// To help make it easier to add custom functionality, the theme emits custom
// events (along with their associated data) related to key theme features.
//
// To enable this functionality update the 'useCustomEvents' variable in
// the theme.liquid file to 'true'.
// ================================================================================

/* eslint-disable no-console */

// ================================================================================
// "Item added to the cart"
//
// - Fires whenever an item has been added to the cart
// - Exposed when the ajax cart is enabled
// - "detail" object data:
//   - The associated product object
// ================================================================================
document.addEventListener("cart:item-added", function (evt) {
  console.log("Item added to the cart");
  console.log(evt.detail.product);
  // The toast itself is triggered by watching the header cart count (see
  // watchCartCountForToast below) so that adds from the product page AND from
  // homepage/collection quick-add all show it. Here we only suppress the cart
  // drawer the product form opens.
  suppressCartDrawer();
  // A same-page AJAX add just succeeded, so there's nothing left for the
  // hard-navigation fallback below to do - clear it so a later, unrelated visit
  // to the cart page doesn't replay this add as a false toast.
  clearPendingCartToastFlag();
});

// The theme has no "toast only" mode: after an add it opens EITHER the confirmation
// popup OR the cart drawer (with the popup disabled, it opens the drawer). The theme
// dispatches `cart:item-added` in the same synchronous tick, immediately after opening
// the drawer, so closing it here runs before the browser paints — the drawer never
// actually appears, leaving only our toast. Opening the drawer from the cart icon is
// untouched. Clicking the theme's own close button runs its full teardown (releases
// the focus trap and body scroll lock).
function suppressCartDrawer() {
  var wrapper = document.querySelector(".quick-cart__wrapper");
  if (!wrapper || !wrapper.classList.contains("active")) return;

  var closeButton = document.querySelector(".quick-cart__close-icon");
  if (closeButton) {
    closeButton.click(); // theme teardown: releases the focus trap
  } else {
    wrapper.classList.remove("active");
  }

  // The drawer locks body scroll immediately on open but defers its unlock by
  // 250ms, and this theme has a history of stuck scroll locks. Since we're closing
  // the drawer we just force-opened, clear every property the lock sets ourselves.
  // We run this twice: once on the next frame, and once AFTER the theme's own 250ms
  // deferred release, so ours is always the final word and the page can never be
  // left unscrollable by this flow. Guarded on the drawer being closed so it never
  // unlocks the page behind a genuine overlay.
  var releaseScrollLock = function () {
    if (document.querySelector(".quick-cart__wrapper.active")) return; // real drawer open
    var body = document.body;
    var html = document.documentElement;
    body.style.overflow = "";
    body.style.paddingRight = "";
    body.style.position = "";
    body.style.top = "";
    body.style.width = "";
    html.style.overflow = "";
  };
  requestAnimationFrame(releaseScrollLock);
  setTimeout(releaseScrollLock, 350);
}

// Shows the top-center "Item added to cart" toast (see snippets/cart-toast.liquid
// and the .cart-toast styles in assets/custom.css). Restarts the auto-hide timer
// on rapid, repeated adds so the toast never flickers off mid-sequence, and pauses
// it while the pointer is over the toast so the "View Cart" link stays reachable.
var CART_TOAST_DURATION = 1500;
var cartToastTimer = null;
var cartToastBound = false;

// Guards against the success-toast watcher (watchCartCountForToast, below)
// stomping an error toast that's already showing - see that function's
// comment for the full race.
var SUPPRESS_SUCCESS_TOAST_MS = 3000;
var suppressSuccessToastUntil = 0;

// Forces the same "cart:updated" refresh a normal successful add triggers -
// used below to make the header/drawer catch up after a request that
// mutated the cart but didn't go through theme.js's own success path (a 422
// partial-fulfillment, or the custom no-navigation add-to-cart submit).
//
// MUST fetch and pass the real cart (not emit bare). theme.js has its own
// "cart:updated" bus subscriber that unconditionally does
// `window.theme.cartData = payload.cart` (see assets/theme.js, "Maintain a
// local representation of the cart accessible to JS") - emitting with no
// payload (as this used to do) hands that subscriber `undefined`, which
// clobbers the real cart data to undefined right then. Every stock-limit
// check after that (limitQuantityToAvailableStock, below) reads a missing
// window.theme.cartData as "0 of this variant already in cart" and
// re-enables the stepper/Add to Cart past the shopper's real ceiling - with
// no reload needed to trigger it, just one of these code paths firing.
function emitCartUpdatedWithFreshCart() {
  if (typeof window.emit !== "function") return;
  fetch("/cart.js", { credentials: "include" })
    .then(function (res) {
      return res.json();
    })
    .then(function (cart) {
      window.emit("cart:updated", { cart: cart });
    })
    .catch(function () {});
}

function hideCartToast(toast) {
  toast.classList.remove("is-visible");
  toast.setAttribute("aria-hidden", "true");
  cartToastTimer = null;
}

function startCartToastTimer(toast) {
  if (cartToastTimer) clearTimeout(cartToastTimer);
  cartToastTimer = setTimeout(function () {
    hideCartToast(toast);
  }, CART_TOAST_DURATION);
}

// Keep the toast pinned near the top of the viewport (its CSS `top`), rather
// than below the full header stack (announcement bar + header + nav), which
// pushed it down far enough to look like it was floating mid-page. Its z-index
// already sits above the header, so overlapping it here is fine.
function positionCartToast(toast) {
  toast.style.top = "";
}

// Remembers the default success message rendered by the snippet, so we can
// restore it after showing an error message.
var cartToastSuccessText = null;

// showCartToast()                     -> success ("Item added to cart" + View Cart)
// showCartToast({ error: true, message }) -> error (red, X icon, no View Cart)
function showCartToast(options) {
  var toast = document.querySelector("[data-cart-toast]");
  if (!toast) return;

  var textEl = toast.querySelector("[data-cart-toast-text]");
  if (cartToastSuccessText === null && textEl) {
    cartToastSuccessText = textEl.textContent;
  }

  var isError = !!(options && options.error);
  if (isError) {
    toast.classList.add("cart-toast--error");
    if (textEl) textEl.textContent = (options && options.message) || "Product not available";
  } else {
    toast.classList.remove("cart-toast--error");
    if (textEl && cartToastSuccessText !== null) textEl.textContent = cartToastSuccessText;
  }

  positionCartToast(toast);
  toast.classList.add("is-visible");
  toast.setAttribute("aria-hidden", "false");

  // Pause the countdown on hover, resume on leave (bound once).
  if (!cartToastBound) {
    toast.addEventListener("mouseenter", function () {
      if (cartToastTimer) {
        clearTimeout(cartToastTimer);
        cartToastTimer = null;
      }
    });
    toast.addEventListener("mouseleave", function () {
      if (toast.classList.contains("is-visible")) startCartToastTimer(toast);
    });
    cartToastBound = true;
  }

  startCartToastTimer(toast);
}

// Used by watchForHardNavigationCartAdd below to remember "an add-to-cart button
// was just clicked" across a full page navigation (sessionStorage survives it,
// unlike any in-memory JS state). Cleared whenever a same-page AJAX add succeeds
// elsewhere in this file, so a stale click can't replay as a false toast later.
var PENDING_CART_TOAST_KEY = "rb:pendingCartToast";

function setPendingCartToastFlag() {
  try {
    sessionStorage.setItem(PENDING_CART_TOAST_KEY, String(Date.now()));
  } catch (e) {
    // sessionStorage unavailable (private browsing, etc.) - nothing we can do.
  }
}

function clearPendingCartToastFlag() {
  try {
    sessionStorage.removeItem(PENDING_CART_TOAST_KEY);
  } catch (e) {
    // sessionStorage unavailable - nothing to clear.
  }
}

// ================================================================================
// "Cart updated"
//
// - Fires whenever the cart is updated
// - Exposed when the ajax cart is enabled
// - "detail" object data:
//   - The cart object
// ================================================================================
document.addEventListener("cart:updated", function (evt) {
  console.log("Cart updated");
  console.log(evt.detail.cart);
});

// ================================================================================
// Toast on EVERY add-to-cart (product page, homepage/collection quick-add,
// quick-view, cross-sells).
//
// The different add paths don't share one DOM event we can listen to (quick-add
// only fires the theme's internal bus). But every add makes the header cart count
// go up, so we watch the header count and show the toast whenever it increases.
// Removals/quantity edits lower or keep the count, so they never trigger it.
// ================================================================================
(function watchCartCountForToast() {
  function readCartCount() {
    var el = document.querySelector("[data-cart-count]");
    if (el) {
      var n = parseInt(el.getAttribute("data-cart-count"), 10);
      if (!isNaN(n)) return n;
    }
    var badge = document.querySelector(".header__cart-link-count");
    if (badge) {
      var m = parseInt(badge.textContent, 10);
      if (!isNaN(m)) return m;
    }
    return 0;
  }

  var lastCartCount = readCartCount();
  var headerCart = document.querySelector("[data-header-cart]");
  // Observe a container that survives the header re-render, so the observer
  // stays valid even when the count element itself is replaced.
  var scope =
    (headerCart && headerCart.closest(".shopify-section")) ||
    document.querySelector(".header") ||
    document.body;

  var observer = new MutationObserver(function () {
    var count = readCartCount();
    // A "maximum reached"/out-of-stock error (see the "cart:error" handler
    // below) forces the exact same quick-cart:updated/cart:updated refresh a
    // real successful add would trigger, so Shopify's actually-applied
    // (capped) quantity shows up on screen. That refresh re-renders the
    // header cart count from scratch, which this observer sees as a plain
    // "count went up" and - with nothing to tell the two apart - used to
    // show the GREEN "Item added to cart" toast right on top of the RED error
    // toast the cart:error handler had just shown, a few ms earlier, for the
    // exact same click. Skip the success toast while that suppression window
    // is active so the error toast is the one the shopper actually sees.
    if (count > lastCartCount && Date.now() >= suppressSuccessToastUntil) showCartToast();
    lastCartCount = count;
  });
  observer.observe(scope, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["data-cart-count"],
    characterData: true,
  });
})();

// ================================================================================
// "Cart error"
//
// - Fires whenever there is an error when adding an item to the cart
//   - Typically due to a product not having sufficient stock
// - "detail" object data:
//   - The error message
// ================================================================================
document.addEventListener("cart:error", function (evt) {
  console.log("Cart error");
  console.log(evt.detail.errorMessage);
  // Block the success-toast watcher (watchCartCountForToast, above) from
  // overwriting the error toast below with "Item added to cart" once the
  // forced refresh a few lines down updates the header cart count. Set
  // BEFORE showCartToast so there's no window for that watcher to fire first.
  suppressSuccessToastUntil = Date.now() + SUPPRESS_SUCCESS_TOAST_MS;

  // Show the error toast (red, X icon, no View Cart) — e.g. when the product is
  // out of stock or already at its maximum available quantity in the cart.
  var message = (evt.detail && evt.detail.errorMessage) || "Product not available";
  showCartToast({ error: true, message: message });

  // Shopify's /cart/add.js and /cart/change.js don't reject a quantity that
  // exceeds availability outright - they cap it to whatever's actually in
  // stock, ADD/APPLY that capped amount, and report the whole thing as a 422
  // ("Only 3 items were added to your cart due to availability."). But
  // theme.js's cart.addItem()/changeItem() treat any 422 as a hard failure:
  // they throw before reaching the line that would normally fire
  // "cart:updated"/"quick-cart:updated" (see addItem/changeItem in
  // assets/theme.js), so the drawer, cart page, and header count are left
  // showing the stale pre-request quantity even though Shopify's cart was
  // genuinely mutated. Force the same refresh a successful request would
  // have triggered, so what's on screen matches what the message just said
  // actually happened.
  if (typeof window.emit === "function") {
    window.emit("quick-cart:updated");
    emitCartUpdatedWithFreshCart();
  }

  // The theme's own product-form (assets/theme.js's onFormSubmit) also writes
  // this same message into the inline field error under the price
  // (snippets/product-block-buy-buttons.liquid's [data-quantity-error]) - it
  // duplicates what the toast above already shows, so hide it and leave only
  // the toast. Deferred to the next tick: theme.js sets this text in its own
  // cart.addItem().catch() handler, which can still run after this same
  // "cart:error" event finishes dispatching - hiding it synchronously here
  // would just get undone a moment later by that handler un-hiding it again.
  setTimeout(function () {
    var inlineError = document.querySelector("[data-quantity-error]");
    if (inlineError) inlineError.classList.add("hidden");
  }, 0);
});

// ================================================================================
// Product page quantity stepper — disable "+" and Add to Cart at available stock
//
// The main product form's quantity selector (snippets/quantity-input.liquid,
// wired up by theme.js's quantityInputInit) just increments/decrements the
// displayed number locally - it has no concept of stock at all, so a shopper
// could step it past what's actually available with no feedback until Add to
// Cart time. Per product decision (matching a reference storefront's
// behavior): once the count reaches the available stock, disable the "+"
// button outright (clicks stop registering entirely, not "increment then
// snap back") and disable Add to Cart too, instead of letting the click go
// through and reporting a failure afterward.
//
// Skipped for variants with inventory_policy "continue" (the store has
// explicitly allowed backorders for that variant - no ceiling to enforce) and
// untracked-inventory variants (no stock figure to compare against). The
// ceiling is the variant's total stock MINUS whatever of it is already
// sitting in this shopper's cart (read from window.theme.cartData, the same
// live cart mirror theme.js itself keeps updated on every "cart:updated") -
// without that subtraction, a shopper who already has all available stock in
// their cart could still step the product-page quantity up and submit, since
// the raw inventory figure alone still looks like there's room. This is still
// only a client-side convenience cap, not the source of truth - the actual
// add is still validated server-side (see the "cart:error" handler and
// fixAddToCartNavigation's own error handling above), so a stale/optimistic
// read here can never let an oversell through.
//
// Reads the same per-variant inventory JSON the theme's own low-stock badge
// uses (snippets/inventory-counter.liquid's [data-product-inventory-json])
// rather than a new data source, and the currently selected variant id from
// the form's own [data-variant-select] (the real name="id" field submitted on
// Add to Cart, kept in sync by theme.js on every option change).
//
// Scoped to [data-product-form] specifically - cart line items (drawer/page)
// and the PLP quick-add stepper both write straight to the server on every
// click and already get Shopify's own capping + the "cart:error" toast
// above, so applying this there would just double up the handling.
// ================================================================================
(function limitQuantityToAvailableStock() {
  // Marks a button as one THIS code disabled, so it only ever re-enables a
  // button it disabled itself - never a button theme.js disabled for its own
  // unrelated reason (e.g. a genuinely sold-out/unavailable variant combo).
  var DISABLED_MARKER = "data-stock-limit-disabled";
  // Add to Cart is "soft" disabled instead (a class, not the real [disabled]
  // attribute): a truly disabled button can't be clicked at all, so it could
  // never show the "maximum limit reached" toast a click on it is supposed to
  // surface. This still looks and (via aria-disabled) reads as disabled.
  var SOFT_DISABLED_CLASS = "is-stock-limit-reached";

  function setDisabled(el, shouldDisable) {
    if (!el) return;
    if (shouldDisable) {
      el.setAttribute(DISABLED_MARKER, "true");
      el.disabled = true;
    } else if (el.getAttribute(DISABLED_MARKER) === "true") {
      el.removeAttribute(DISABLED_MARKER);
      el.disabled = false;
    }
  }

  function setSoftDisabled(el, shouldDisable) {
    if (!el) return;
    if (shouldDisable) {
      el.classList.add(SOFT_DISABLED_CLASS);
      el.setAttribute("aria-disabled", "true");
    } else if (el.classList.contains(SOFT_DISABLED_CLASS)) {
      el.classList.remove(SOFT_DISABLED_CLASS);
      el.removeAttribute("aria-disabled");
    }
  }

  // The "Inventory status" block (snippets/inventory-counter.liquid, which
  // renders [data-product-inventory-json]) is a sibling block alongside the
  // product form within the section - not a descendant of the <form> itself
  // (see snippets/product-blocks.liquid) - so it has to be looked up from the
  // shared section ancestor, same as theme.js's own productBlocks(container)
  // does for both quantityInputInit and inventoryCounterElInit. The quick
  // view/quick add modal (snippets/quick-product-modal.liquid) renders the
  // same product form markup via AJAX, but its content isn't wrapped in a
  // .shopify-section at all - only in [data-quick-product-modal] - so that
  // first fallback missed it entirely and fell through to `form` itself,
  // which also doesn't contain the sibling inventory block. Left
  // unresolved, this returned null inventory data, and the stock-limit
  // checks below then treated the modal's Add to Cart as having "no ceiling
  // to enforce" - letting a shopper type/step it past the real stock with
  // no toast and no disable, even though the real product page a click away
  // enforced it correctly.
  function getVariantInventory(form, variantId) {
    var section = form.closest(".shopify-section") || form.closest("[data-quick-product-modal]") || form;
    var jsonEl = section.querySelector("[data-product-inventory-json]");
    if (!jsonEl) return null;

    var data;
    try {
      data = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return null;
    }
    return (data && data.inventory && data.inventory[variantId]) || null;
  }

  function getCartQuantityForVariant(variantId) {
    var cartData = window.theme && window.theme.cartData;
    var items = cartData && cartData.items;
    if (!items) return 0;

    var targetId = parseInt(variantId, 10);
    return items.reduce(function (total, item) {
      return item.variant_id === targetId ? total + item.quantity : total;
    }, 0);
  }

  // Keeps the "(N in cart)" label (snippets/quantity-input.liquid's
  // [data-cart-item-count], shown when the block enables
  // show_quantity_in_cart) in sync with the same live cart data this file
  // already tracks for the stock ceiling. theme.js has its own live-update
  // path for this label (updateQuantitySelectorCounts, fired from its
  // addItem()/changeItem()), but it's only reliable for adds that go through
  // theme.js's own submit handler - this file's custom no-navigation submit
  // path (fixAddToCartNavigation, below) and the cart:error 422 refresh both
  // mutate the cart without going through it, leaving this label stuck at
  // whatever count was server-rendered at page load. Left stale, a shopper
  // (or QA) sees Add to Cart disabled with no visible reason - it reads as a
  // bug rather than "you already have N of these in your cart."
  function syncCartCountLabel(form, variantId) {
    var label = form.querySelector("[data-cart-item-count]");
    if (!label) return;
    var count = getCartQuantityForVariant(variantId);
    label.setAttribute("data-cart-item-count", count);
    var countEl = label.querySelector(".item_count");
    if (countEl) countEl.textContent = count;
  }

  function sync(form) {
    var addButton = form.querySelector("[data-add-quantity]");
    var addToCartButtons = form.querySelectorAll("[data-add-to-cart]");
    var quantityInput = form.querySelector("[data-quantity-input]");
    if (!quantityInput) return;

    var variantSelect = form.querySelector("[data-variant-select]");
    var variantId = variantSelect && variantSelect.value;
    var inventory = variantId && getVariantInventory(form, variantId);

    if (variantId) syncCartCountLabel(form, variantId);

    // No ceiling to enforce (no variant, no inventory data, untracked stock,
    // or backorders explicitly allowed) - make sure nothing is left disabled
    // from a previously selected variant that DID have one.
    if (
      !inventory ||
      inventory.inventory_management !== "shopify" ||
      inventory.inventory_policy === "continue"
    ) {
      setDisabled(addButton, false);
      addToCartButtons.forEach(function (btn) {
        setSoftDisabled(btn, false);
      });
      return;
    }

    var currentValue = parseInt(quantityInput.value, 10);
    if (isNaN(currentValue)) return;

    var alreadyInCart = getCartQuantityForVariant(variantId);
    var available = Math.max(inventory.inventory_quantity - alreadyInCart, 0);
    // Both buttons disable only once the maximum has actually been reached -
    // i.e. there's nothing left of this variant to add at all (available
    // <= 0) - not merely because the stepper's CURRENT value happens to
    // match what's left. Using currentValue >= available here (matching the
    // stepper's own displayed number against the ceiling) disabled "+" one
    // unit too early: with 1 left, currentValue starts at 1, so 1 >= 1
    // disabled it immediately even though stock wasn't actually exhausted
    // yet. The shopper can still freely step past what's left - Add to Cart
    // (and the "maximum limit reached" toast on a blocked click) is what
    // actually enforces the ceiling at submit time.
    var atLimit = available <= 0;
    setDisabled(addButton, atLimit);
    var cartAtLimit = currentValue > available;
    addToCartButtons.forEach(function (btn) {
      setSoftDisabled(btn, cartAtLimit);
    });

    // Native hint for the field itself (spinner arrows, mobile numeric
    // keypads with a built-in ceiling) - belt-and-suspenders alongside the
    // "change" listener's own clamp below, which is what actually enforces
    // this for direct keyboard entry (a max attribute alone doesn't stop a
    // browser from accepting a typed value above it).
    quantityInput.setAttribute("max", Math.max(available, 1));
  }

  function syncAllForms() {
    document.querySelectorAll("[data-product-form]").forEach(sync);
  }

  document.addEventListener("click", function (evt) {
    var button = evt.target.closest("[data-add-quantity]");
    if (!button) return;

    var form = button.closest("[data-product-form]");
    if (!form) return;

    // theme.js's own click handler is bound directly on this button, so it
    // runs (and increments the input's value) before this document-level
    // listener sees the bubbled event - by the time sync() reads the input,
    // the increment has already happened.
    sync(form);
  });

  // "-" needs this same direct sync - it can't rely on the "change" listener
  // below (observed live: clicking "-" never re-enabled a disabled Add to
  // Cart). theme.js's handleAddQuantity/handleSubtractQuantity both do
  // `quantityInput.dispatchEvent(new Event("change"))` after updating the
  // value, but a plain `new Event("change")` defaults to bubbles:false - it
  // never reaches a document-level listener in the bubble phase, on "-" OR
  // "+". "+" only ever appeared to work because of ITS OWN direct click
  // listener above; "-" had no equivalent, so reducing the quantity back
  // below the ceiling never re-checked it and left Add to Cart stuck
  // disabled.
  document.addEventListener("click", function (evt) {
    var button = evt.target.closest("[data-subtract-quantity]");
    if (!button) return;

    var form = button.closest("[data-product-form]");
    if (!form) return;

    sync(form);
  });

  // Add to Cart is soft-disabled (see setSoftDisabled above) specifically so
  // a click on it still fires - caught here, in the capture phase, before
  // theme.js's own click-triggered form submission (and before
  // fixAddToCartNavigation's "submit" listener further up this file, since
  // preventing a submit button's click here also prevents the form submit
  // it would otherwise trigger). Blocks the add outright: no server request,
  // no cart mutation, no success toast - only this toast.
  document.addEventListener(
    "click",
    function (evt) {
      var button = evt.target.closest("[data-add-to-cart]");
      if (!button || !button.classList.contains(SOFT_DISABLED_CLASS)) return;

      evt.preventDefault();
      evt.stopImmediatePropagation();

      // Locale-backed string, but locales/en.default.json is regenerated by
      // Shopify's own admin language editor and has already been observed to
      // drop this exact key on a regen - when that happens `| t` renders the
      // literal "Translation missing: ..." text instead of erroring, which is
      // truthy and would otherwise sail straight past a plain `||` fallback
      // and show that raw string to shoppers. Mirrors the same guard
      // snippets/cart-toast.liquid already uses for its own locale strings.
      var strings = window.theme && window.theme.strings && window.theme.strings.products.product;
      var message = strings && strings.quantityLimitReached;
      if (!message || message.indexOf("Translation missing") !== -1) {
        message = "Maximum quantity limit reached";
      }

      showCartToast({ error: true, message: message });
    },
    true
  );

  // Typing a number directly into the MAIN PRODUCT FORM's quantity field
  // bypasses the stepper buttons entirely, so nothing was clamping it - a
  // shopper could type 5, 500, whatever, regardless of actual stock (see
  // this variant's inventory JSON, same source the rest of this file already
  // uses). Clamps down to what's actually left (or 1, the field's own
  // floor, if nothing's left). This variant needs live cart awareness
  // (available = stock minus whatever's already in the shopper's cart)
  // because the value here means "how many more to add", not "the line's
  // total quantity" - see clampToStaticMax below for the cart's own
  // quantity inputs, where that distinction doesn't apply.
  function clampTypedQuantityDynamic(input, form) {
    var variantSelect = form.querySelector("[data-variant-select]");
    var variantId = variantSelect && variantSelect.value;
    var inventory = variantId && getVariantInventory(form, variantId);
    if (!inventory || inventory.inventory_management !== "shopify" || inventory.inventory_policy === "continue") {
      return;
    }
    var typedValue = parseInt(input.value, 10);
    var alreadyInCart = getCartQuantityForVariant(variantId);
    var available = Math.max(inventory.inventory_quantity - alreadyInCart, 0);
    var ceiling = Math.max(available, 1);
    if (!isNaN(typedValue) && typedValue > ceiling) {
      input.value = ceiling;
    }
  }

  // Cart line item quantity inputs (drawer and /cart page - see
  // snippets/cart-item.liquid and quick-cart-item.liquid) aren't inside a
  // [data-product-form] at all, and their value IS the line's total
  // quantity already, not "how many more" - so they render a plain static
  // ceiling (data-quantity-max, set from the variant's own tracked stock at
  // render time - see quantity-input.liquid's max_value param) instead of
  // needing the cart-aware math above. Typing past it wasn't blocked by
  // anything: Shopify's /cart/change.js does cap the actual line quantity
  // server-side, but the FIELD itself would keep showing whatever oversized
  // number was typed until that response came back.
  function clampToStaticMax(input) {
    var max = input.getAttribute("data-quantity-max");
    if (max === null) return;
    var ceiling = Math.max(parseInt(max, 10), 1);
    var typedValue = parseInt(input.value, 10);
    if (!isNaN(typedValue) && typedValue > ceiling) {
      input.value = ceiling;
    }
  }

  // Fires on every keystroke, not just on blur/Enter - a shopper could still
  // see (and briefly submit off of) an out-of-range number sitting in the
  // field between typing it and leaving the field if this only clamped on
  // "change". Native, user-driven "input" events always bubble (unlike
  // theme.js's synthetic, non-bubbling "change" dispatches from
  // handleAddQuantity/handleSubtractQuantity - see the "change" listener
  // below), so this is likewise only ever reached by real keyboard entry.
  document.addEventListener("input", function (evt) {
    var input = evt.target.closest("[data-quantity-input]");
    if (!input) return;

    clampToStaticMax(input);

    var form = input.closest("[data-product-form]");
    if (!form) return;
    clampTypedQuantityDynamic(input, form);
    sync(form);
  });

  // Covers manual typing in the quantity input. In practice this is ALSO the
  // only place manual typing needs handling: theme.js's own
  // handleAddQuantity/handleSubtractQuantity dispatch their "change" via
  // `new Event("change")`, which defaults to bubbles:false and so never
  // reaches this document-level listener at all - only a real, native
  // "change" from the user directly editing the field ever lands here (see
  // the dedicated click listeners above for "+"/"-" instead). Kept alongside
  // the "input" listener above as a fallback (e.g. a pasted value, or any
  // path that fires "change" without a preceding "input").
  document.addEventListener("change", function (evt) {
    var input = evt.target.closest("[data-quantity-input]");
    if (!input) return;

    clampToStaticMax(input);

    var form = input.closest("[data-product-form]");
    if (!form) return;
    clampTypedQuantityDynamic(input, form);
    sync(form);
  });

  // Switching variants changes the available stock entirely - re-check every
  // product form on the page (there's normally exactly one; a quick-view
  // modal could add a second). Deferred a tick since this event fires from
  // inside theme.js's own variant-change handling, before it's necessarily
  // finished writing the new variant id to [data-variant-select].
  document.addEventListener("product:variant-change", function () {
    setTimeout(syncAllForms, 0);
  });

  // The quick view/quick add modal (snippets/quick-product-modal.liquid)
  // fetches and injects its product form via AJAX well after this script's
  // initial syncAllForms() pass - that form was never checked at all until
  // the shopper mutated its quantity/cart themselves, which is exactly why
  // it could sit there already past the stock ceiling (no disable, no
  // toast) the instant it finished loading. theme.js dispatches this DOM
  // event once the fetched content is actually in the DOM.
  document.addEventListener("quick-view:loaded", function () {
    setTimeout(syncAllForms, 0);
  });

  // Any cart mutation changes how much of this variant is "already in cart",
  // which shifts the remaining ceiling - e.g. removing this item from the
  // cart drawer while its product page sits open behind it should re-enable
  // Add to Cart. theme.js's own cart.changeItem()/addItemById() only fire
  // this on its internal pub-sub bus (window.on), not as a DOM CustomEvent -
  // addItem() is the only one of the three that also dispatches a real DOM
  // event - so subscribe via the bus to catch all three. Deferred a tick:
  // window.theme.cartData (read by getCartQuantityForVariant) is written by
  // theme.js's OWN "cart:updated" bus subscription, and subscriber order
  // between separate files isn't guaranteed, so this could otherwise read
  // the stale cart on the same tick it changed.
  //
  // window.on itself is assigned by theme.js (a bundled ES module) as part of
  // its own top-level init - which, depending on module/chunk timing, is not
  // guaranteed to have run yet by the time THIS script's top-level code
  // executes, even though this <script> tag sits after theme.js's in the
  // page (observed live: window.on was still undefined at this point, so the
  // check below silently skipped the subscription forever - no retry, no
  // fallback - and every later add-to-cart/quantity-change left
  // window.theme.cartData stale, which let the stepper/Add to Cart re-enable
  // past the real stock ceiling without a page reload). Poll briefly instead
  // of a single one-shot check, so a slow-to-initialize theme.js still gets
  // subscribed correctly.
  (function subscribeToCartUpdatedBus(attemptsLeft) {
    if (typeof window.on === "function") {
      window.on("cart:updated", function () {
        setTimeout(syncAllForms, 0);
      });
      return;
    }
    if (attemptsLeft <= 0) return;
    setTimeout(function () {
      subscribeToCartUpdatedBus(attemptsLeft - 1);
    }, 100);
  })(50);

  // Belt-and-suspenders for the main product form's own addItem() path
  // specifically: it also dispatches a real DOM "cart:updated" CustomEvent
  // (see assets/theme.js) carrying the fresh cart as evt.detail.cart, unlike
  // the other two mutation paths above. Applying that directly here means
  // this stays correct even if the bus subscription above is still mid-retry.
  document.addEventListener("cart:updated", function (evt) {
    if (evt.detail && evt.detail.cart) window.theme.cartData = evt.detail.cart;
    setTimeout(syncAllForms, 0);
  });

  // theme.js populates window.theme.cartData for the FIRST time via its own
  // cart.get().then(...) on page load (see assets/theme.js, "Maintain a local
  // representation of the cart accessible to JS") - a plain promise
  // assignment with no event dispatched when it resolves. On a fresh page
  // load (or reload) that promise is still pending at the point syncAllForms
  // runs below, so getCartQuantityForVariant reads window.theme.cartData as
  // undefined and this treats the variant as having nothing in the cart yet -
  // understating what's already there and leaving the stepper/Add to Cart
  // wrongly enabled past the real ceiling. There's no subsequent "cart:updated"
  // to correct it either, since that only fires on an actual mutation - not
  // when theme.js's initial fetch finishes. Fetch the live cart ourselves so
  // this re-syncs the instant real data is available, independent of whether
  // theme.js's own promise has resolved yet.
  fetch("/cart.js", { credentials: "include" })
    .then(function (res) {
      return res.json();
    })
    .then(function (cartData) {
      if (!window.theme.cartData) window.theme.cartData = cartData;
      syncAllForms();
    })
    .catch(function () {});

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", syncAllForms);
  } else {
    syncAllForms();
  }
})();

// ================================================================================
// Quick Add modal — variant/custom-option toast
//
// The Quick Add modal's per-variant "Add"/quantity-stepper button
// (data-add-quantity, see snippets/quick-add-modal.liquid) calls the theme's
// Zt.addItemById() cart helper directly (assets/theme.js) — unlike the main
// product form's addItem(), it never dispatches the `cart:item-added` DOM event
// and only emits internal bus events, so the header cart-count refresh (and thus
// the count-watching toast above) can lag or be missed for this flow. This is a
// direct, reliable trigger for that same case.
//
// `data-add-quantity` is the theme's OWN selector for "add one unit" (used both
// for the initial add and the +1 stepper once the item is already in the cart) -
// it is never bound to a decrement, so this can never fire on a removal or
// quantity decrease.
// ================================================================================
(function watchQuickAddModalForToast() {
  var pendingQuickAdd = false;

  document.addEventListener("click", function (evt) {
    if (evt.target.closest && evt.target.closest("[data-add-quantity]")) {
      pendingQuickAdd = true;
    }
  });

  if (typeof window.on === "function") {
    window.on("quick-cart:updated", function () {
      if (!pendingQuickAdd) return;
      pendingQuickAdd = false;

      // theme.js's handleError() dispatches the "cart:error" DOM event (which
      // synchronously sets suppressSuccessToastUntil and forces this exact
      // "quick-cart:updated" emit to refresh the stale drawer - see the
      // "cart:error" listener above) BEFORE it gets around to emitting
      // "quick-add:error" a few lines later in the same function. That means
      // for a capped/rejected quantity change, this handler runs while
      // pendingQuickAdd is still true and "quick-add:error" hasn't cleared it
      // yet, so without this check it fired the green "Item added to cart"
      // toast directly on top of the red error toast that had just been shown
      // for the very same click.
      if (Date.now() < suppressSuccessToastUntil) return;

      clearPendingCartToastFlag();
      showCartToast();
    });

    // A failed add (e.g. now out of stock) already shows the theme's error toast
    // via the `cart:error` listener above - just clear the pending flag so a later
    // unrelated success doesn't get miscredited to this click.
    window.on("quick-add:error", function () {
      pendingQuickAdd = false;
    });
  }
})();

// ================================================================================
// Toast survives a hard navigation to the cart page
//
// Some product customization fields (e.g. a required custom-option/personalization
// selection in the product form, shown in the product page's Quick View modal)
// validate the selection before adding to cart, and do so by calling the form's
// plain `.submit()`. `.submit()` performs a real browser form submission WITHOUT
// firing a "submit" DOM event, so neither the theme's own AJAX add-to-cart nor any
// listener in this file ever sees it - the browser just POSTs straight to the
// form's action and Shopify redirects to the cart page. The toast never gets a
// chance to render because the page unloads before it can.
//
// We can't intercept that submission, but sessionStorage survives the navigation:
// remember that the theme's own Add to Cart button (data-add-to-cart, see
// snippets/product-form.liquid) was clicked, and show the toast once on the next
// page load if it's the cart page. The flag self-expires after a few seconds so a
// click that never actually navigates (e.g. failed validation) can't cause a false
// toast on some unrelated later visit to the cart page.
// ================================================================================
(function watchForHardNavigationCartAdd() {
  var FLAG_TTL_MS = 15000;

  document.addEventListener(
    "click",
    function (evt) {
      if (evt.target.closest && evt.target.closest("[data-add-to-cart]")) {
        setPendingCartToastFlag();
      }
    },
    true
  );

  if (document.body.className.indexOf("template-cart") === -1) return;

  var raw;
  try {
    raw = sessionStorage.getItem(PENDING_CART_TOAST_KEY);
  } catch (e) {
    return;
  }
  clearPendingCartToastFlag();
  if (!raw) return;

  var age = Date.now() - parseInt(raw, 10);
  if (isNaN(age) || age < 0 || age > FLAG_TTL_MS) return;

  showCartToast();
})();

// ================================================================================
// "Quick cart opened"
//
// - Fires whenever the quick cart is opened
// - Exposed when the ajax cart is enabled
// - "detail" object data:
//   - The cart object
// ================================================================================
document.addEventListener("quick-cart:open", function (evt) {
  console.log("Quick cart opened");
  console.log(evt.detail.cart);
});

// ================================================================================
// "Quick cart closed"
//
// - Fires whenever the quick cart is opened
// - Exposed when the ajax cart is enabled
// ================================================================================
document.addEventListener("quick-cart:close", function () {
  console.log("Quick cart closed");
});

// ================================================================================
// "Product variant changed"
//
// - Fires whenever a variant product is selected
// - Exposed when a 'Variant selectors' block has been added to a product template
//   or featured product section
// - "detail" object data:
//   - The variant object
// ================================================================================
document.addEventListener("product:variant-change", function (evt) {
  console.log("Product variant changed");
  console.log(evt.detail.variant);
});

// ================================================================================
// "Product quantity updated"
//
// - Fires whenever a product quanatiy is updated
// - Exposed when a 'Quantity selector' block has been added to a product template
//   or featured product section
// - "detail" object data:
//   - The variant object
//   - The quantity
// ================================================================================
document.addEventListener("product:quantity-update", function (evt) {
  console.log("Product quantity updated");
  console.log(evt.detail.quantity, evt.detail.variant);
});

// ================================================================================
// "Quick view loaded"
//
// - Fires whenever a quick view modal is opened
// - Exposed when the 'Enable quick view' setting is enabled and a quick view
//   modal is opened
// ================================================================================
document.addEventListener("quick-view:loaded", function () {
  console.log("Quick view loaded");
});
/* eslint-enable no-console */

// Note: the stuck-scroll-lock watchdog lives in its own file,
// assets/scroll-lock-safety.js (loaded from snippets/theme-globals.liquid).

// ================================================================================
// PLP / search — sticky filter & sort bar height tracking
//
// assets/theme.css has a custom appended block making .filter-topbar-wrap
// (snippets/filter-topbar.liquid) sticky, and adjusting the theme's own
// sticky filter-sidebar offset (data-sticky-container) to sit below it instead
// of being covered by it. This keeps --filter-topbar-height in sync with the
// topbar's actual rendered height (it can grow, e.g. when active-filter chips
// wrap to a second line) - the same ResizeObserver-driven-CSS-var approach the
// theme itself uses for --height-header.
// ================================================================================
(function trackFilterTopbarHeight() {
  function measure() {
    var topbar = document.querySelector(".filter-topbar-wrap");
    var height = topbar ? topbar.offsetHeight : 0;
    document.documentElement.style.setProperty("--filter-topbar-height", height + "px");
  }

  function start() {
    measure();
    if (typeof ResizeObserver !== "undefined") {
      // Observe <body> rather than the topbar itself: re-reads the topbar
      // fresh on every call, so it keeps working even if the topbar element
      // is ever replaced (e.g. a full section re-render), with no need to
      // detect/rebind to a new node.
      new ResizeObserver(measure).observe(document.body);
    } else {
      window.addEventListener("resize", measure);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();

// ================================================================================
// Cart recommendations — Quick View
//
// snippets/cart-recommendations-fallback.liquid renders a quick-view button
// (data-cart-rec-quick-view) on each "You may also like" card in the cart page
// and drawer. The theme's own quick-view modal (assets/theme.js) does NOT
// listen for a DOM "quick-view:open" CustomEvent - it subscribes through the
// theme's internal pub/sub bus (window.on / window.emit, exposed by
// assets/theme.js), which is a separate mechanism from document-level
// CustomEvents. product-item's own quick-view button triggers that same
// bus via window.emit(...), so this must use window.emit too, not
// document.dispatchEvent, or the modal never opens.
//
// Bound DIRECTLY on each button (not a document-level delegate): the drawer
// loads several third-party app scripts (bundles, loyalty, reviews, product
// options), and a document-level listener depends on the click event actually
// propagating all the way up from the button to `document` - something in
// that chain can intercept it first. The theme's OWN working cross-sell
// buttons (Add to Cart / Choose options in this same card) avoid that risk
// entirely by binding straight to the element, so this does the same.
//
// Rebinding is driven by a MutationObserver on each [data-cross-sells]
// container, NOT the "cart:updated" event. The quick-cart's own
// `refreshQuickCart()` (assets/theme.js) replaces the cross-sells container's
// innerHTML via an ASYNC fetch that resolves well after "cart:updated" fires -
// binding on "cart:updated" re-bound the old, about-to-be-replaced buttons
// and missed the fresh ones from the fetch response, which is why quick-view
// worked once and then stopped after the first cart mutation. A
// MutationObserver reacts to the actual DOM swap instead of guessing timing.
// ================================================================================
(function bindCartRecQuickView() {
  /* eslint-disable no-console */
  var DEBUG = false;
  var bound = typeof WeakSet !== "undefined" ? new WeakSet() : null;

  function openQuickView(button) {
    var productUrl = button.getAttribute("data-product-url");
    if (DEBUG) console.log("[cart-rec-quick-view] click received, productUrl =", productUrl);
    if (!productUrl) return;

    var card = button.closest(".cart-rec-card");
    var media = card ? card.querySelector(".cart-rec-card__media") : null;

    if (DEBUG) {
      var modalNode = document.querySelector("[data-quick-product-modal]");
      console.log(
        "[cart-rec-quick-view] modal classList before emit =",
        modalNode ? modalNode.className : "(modal node not found)"
      );
      console.log("[cart-rec-quick-view] dispatching quick-view:open");
    }
    if (typeof window.emit !== "function") return;
    window.emit("quick-view:open", null, {
      type: "quick-view",
      productUrl: productUrl,
      productMedia: media,
      productTitle: button.getAttribute("data-product-title"),
    });
  }

  function bindAll() {
    var buttons = document.querySelectorAll("[data-cart-rec-quick-view]");
    if (DEBUG) console.log("[cart-rec-quick-view] bindAll() found", buttons.length, "button(s)");
    for (var i = 0; i < buttons.length; i++) {
      var button = buttons[i];
      if (bound) {
        if (bound.has(button)) continue;
        bound.add(button);
      } else if (button.dataset.cartRecQuickViewBound === "true") {
        continue;
      } else {
        button.dataset.cartRecQuickViewBound = "true";
      }
      button.addEventListener("click", function (evt) {
        if (DEBUG) console.log("[cart-rec-quick-view] button click handler fired");
        evt.preventDefault();
        openQuickView(evt.currentTarget);
      });
    }
  }

  function observeCrossSells() {
    if (typeof MutationObserver === "undefined") return;
    var containers = document.querySelectorAll("[data-cross-sells]");
    for (var i = 0; i < containers.length; i++) {
      new MutationObserver(bindAll).observe(containers[i], { childList: true, subtree: true });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      bindAll();
      observeCrossSells();
    });
  } else {
    bindAll();
    observeCrossSells();
  }
  document.addEventListener("cart:updated", bindAll);
  document.addEventListener("quick-cart:open", bindAll);
})();

// ================================================================================
// Search — partial/prefix word matching on the full search results page
//
// Shopify's classic /search endpoint only matches whole/stemmed words out of
// the box, so a short query like "min" returns nothing even when a product
// named e.g. "Mint Body Wash" exists. The predictive search dropdown doesn't
// have this problem (Shopify's predictive API always prefix-matches
// internally), so only the full-page result forms need help. Appending a
// trailing "*" wildcard to the last word tells Shopify's search to match it
// as a prefix instead of a whole word.
//
// Only fires on actual form submission (Enter / the submit button), never on
// the "input" event that drives the AJAX predictive dropdown, so that
// separate flow is untouched. sections/main-search.liquid and
// snippets/quick-search.liquid strip the trailing "*" back off wherever they
// echo search.terms, so the visible query/heading still reads "min", not
// "min*".
// ================================================================================
(function enableSearchPrefixMatching() {
  document.addEventListener("submit", function (evt) {
    var form = evt.target;
    if (!form || !form.matches || !form.matches("[data-quick-search-form], .search-form")) return;

    var input = form.querySelector('input[name="q"]');
    if (!input) return;

    var value = input.value.trim();
    if (value !== "" && value.charAt(value.length - 1) !== "*") {
      input.value = value + "*";
    }
  });
})();

// ================================================================================
// Collection/search filter & sort forms — never allow a native full-page submit
//
// sections/filter-topbar.liquid's <form data-filter-form data-filter-topbar>
// (sort options, and the hidden filter inputs kept in sync with it) has no
// <button type="submit"> and no action/method - it's designed to be handled
// entirely by assets/theme.js, which listens for "change" on its inputs and
// re-fetches results via AJAX (see FilterForm/filterBar in theme.js).
//
// That JS already guards ONE way a real submit can slip through: pressing
// Enter inside a price-range number input (see FilterForm's handleSubmit,
// which blurs the input instead). It does NOT guard the same browser behavior
// for the sort radios or filter checkboxes - pressing Enter while one of them
// is focused (e.g. via keyboard/Tab navigation) triggers the form's native
// implicit submission, which reloads the page via a plain GET instead of the
// usual in-place AJAX update. This is most noticeable at 0 results, since
// that's when someone is most likely to keep tabbing/pressing Enter through
// sort options looking for a fix.
//
// Since these forms are never meant to submit natively at all, unconditionally
// preventing "submit" here is correct in every case, not just this one.
// ================================================================================
(function preventNativeFilterFormSubmit() {
  document.addEventListener("submit", function (evt) {
    var form = evt.target;
    if (!form || !form.matches) return;
    if (!form.matches("[data-filter-form]")) return;

    evt.preventDefault();
  });
})();

// ================================================================================
// Collection/search sort — skip the AJAX re-fetch when there are 0 results
//
// With 0 products showing (e.g. a filter combination that matches nothing),
// picking a different sort option still fires filterBar's "change" handler
// in theme.js, which re-fetches the (still empty) result set via AJAX - a
// pointless request that just re-shows the same empty grid behind the sort
// dropdown while it loads. Since there's nothing to sort, intercept the click
// on the sort option BEFORE the radio's default action fires - preventing
// that means the input never actually changes, so no "change" event and no
// fetch ever happens - and tell the shopper why via a toast instead. Opening
// the sort dropdown itself (the toggle button) is untouched; only picking an
// option while empty is blocked.
//
// The live product count lives on [data-partial] (kept in sync on every AJAX
// refresh - see _renderView in theme.js), under a different attribute name on
// collection vs. search pages (data-collection-products-count /
// data-search-results-count - see sections/main-collection-product-grid.liquid
// and sections/main-search.liquid).
// ================================================================================
(function blockSortWhenNoResults() {
  // Keyed by sort group element, since a page can have more than one (e.g.
  // filter drawer vs. topbar) and each toast needs to be positioned relative
  // to its own group.
  var toastsByGroup = new WeakMap();
  var hideTimeout;

  function getProductsCount(sortGroupEl) {
    var sectionEl = sortGroupEl.closest("[data-section-id]");
    var partialEl = sectionEl && sectionEl.querySelector("[data-partial]");
    if (!partialEl) return null;

    var count = partialEl.dataset.collectionProductsCount;
    if (count === undefined) count = partialEl.dataset.searchResultsCount;
    if (count === undefined) return null;

    return parseInt(count, 10);
  }

  function showToast(sortGroupEl, message) {
    var toastEl = toastsByGroup.get(sortGroupEl);
    if (!toastEl) {
      toastEl = document.createElement("div");
      toastEl.className = "filter-sort-toast ff-body";
      toastEl.setAttribute("role", "status");
      toastEl.setAttribute("aria-live", "polite");
      // Mirrors the red-circle-with-X look of the cart's "max quantity
      // reached" error toast (see showCartToast's error branch / the
      // .cart-toast--error styles), so this reads as the same kind of
      // "can't do that right now" message.
      toastEl.innerHTML =
        '<span class="filter-sort-toast__icon" aria-hidden="true">&times;</span>' +
        '<span class="filter-sort-toast__text"></span>';
      sortGroupEl.appendChild(toastEl);
      toastsByGroup.set(sortGroupEl, toastEl);
    }

    toastEl.querySelector(".filter-sort-toast__text").textContent = message;
    toastEl.classList.add("is-visible");

    clearTimeout(hideTimeout);
    hideTimeout = setTimeout(function () {
      toastEl.classList.remove("is-visible");
    }, 3000);
  }

  document.addEventListener(
    "click",
    function (evt) {
      var sortGroupEl = evt.target.closest("[data-topbar-sort-group]");
      if (!sortGroupEl) return;

      var sortItemEl = evt.target.closest("[data-sort-item-input], label[data-sort]");
      if (!sortItemEl) return;

      var productsCount = getProductsCount(sortGroupEl);
      if (productsCount !== 0) return;

      evt.preventDefault();
      evt.stopPropagation();
      showToast(
        sortGroupEl,
        sortGroupEl.dataset.noProductsToSortText ||
          "No products found. Try adjusting your filters before sorting."
      );
    },
    true
  );
})();

// ================================================================================
// Product form — reliable AJAX add-to-cart (no full-page navigation)
//
// theme.js's own ProductForm submit handler (assets/theme.js's onSubmit /
// onFormSubmit) resolves the variant to add by re-serializing every
// [name^="options"] field in the form and matching it against the product's
// variants client-side. When that lookup comes back empty (a mismatched or
// unexpected options field - eg. from a product-options app modifying the
// form), it throws INSIDE the "submit" listener before ever reaching the
// line that calls preventDefault() - and throwing partway through an event
// listener does NOT stop the browser's default action, so the form falls
// through to a real, native POST to /cart/add, which redirects to /cart.
// That's the "Add to Cart navigates to the full cart page" bug.
//
// Rather than depend on that fragile client-side variant re-resolution at
// all, this submits the form exactly as the browser would have (same
// fields, same multipart encoding Shopify's {% form 'product' %} tag sets),
// just via fetch to /cart/add.js instead of a real navigation - so it can't
// be derailed by whatever confuses the theme's own option-matching. Runs in
// the capture phase and stops the theme's own submit listener from also
// running (stopImmediatePropagation), so there's exactly one handler for
// this event and no double-add.
//
// Success is handled entirely through the same events every other add path
// in the theme already uses - "quick-cart:updated" (refresh the drawer),
// "quick-cart:open" (show it), "cart:updated" (refresh the header count) -
// which is also what the existing header-cart-count watcher above is
// already watching to trigger the "Item added" toast. Nothing new to wire
// up for that part; this only replaces how the request itself gets sent.
// ================================================================================
(function fixAddToCartNavigation() {
  document.addEventListener(
    "submit",
    function (evt) {
      var form = evt.target;
      if (!form || !form.matches || !form.matches("[data-product-form]")) return;

      evt.preventDefault();
      evt.stopImmediatePropagation();

      var submitButtons = form.querySelectorAll("[data-add-to-cart]");
      submitButtons.forEach(function (button) {
        button.classList.add("loading");
        button.disabled = true;
      });

      if (typeof window.emit === "function") window.emit("cart:updating");

      fetch("/cart/add.js", {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json" },
        body: new FormData(form),
      })
        .then(function (res) {
          return res.json().then(function (data) {
            return { ok: res.ok, data: data };
          });
        })
        .then(function (_ref) {
          var ok = _ref.ok,
            data = _ref.data;

          submitButtons.forEach(function (button) {
            button.classList.remove("loading");
            button.disabled = false;
          });

          if (!ok || data.status) {
            // This add-to-cart path bypasses theme.js's addItem()/handleError()
            // entirely (see this function's own comment above), so it never
            // goes through the "cart:error" DOM event that normally sets this
            // flag. Without it, the forced refresh below re-renders the header
            // cart count (Shopify's partial-fulfillment case genuinely adds
            // some quantity even on a 422 - see the comment on that refresh),
            // which the success-toast watchers up top would read as "count
            // went up" and show the GREEN "Item added to cart" toast right on
            // top of the RED error toast a few lines down, for the exact same
            // click - the wrong pairing.
            suppressSuccessToastUntil = Date.now() + SUPPRESS_SUCCESS_TOAST_MS;

            // Shopify's own partial-fulfillment case (see the "cart:error"
            // handler above) still mutates the cart even on a 422, so
            // refresh here too rather than leaving stale drawer/header state.
            if (typeof window.emit === "function") {
              window.emit("quick-cart:updated");
              emitCartUpdatedWithFreshCart();
            }
            showCartToast({
              error: true,
              message: (data && data.description) || (data && data.message) || "Product not available",
            });
            return;
          }

          var purchaseConfirmation = document.querySelector("[data-purchase-confirmation-popup]");
          if (typeof window.emit === "function") {
            window.emit("quick-cart:updated");
            if (purchaseConfirmation) {
              window.emit("confirmation-popup:open", { product: data });
            } else {
              window.emit("quick-cart:open");
            }
            emitCartUpdatedWithFreshCart();
          }
          document.dispatchEvent(new CustomEvent("cart:item-added", { detail: { product: data } }));
          clearPendingCartToastFlag();
        })
        .catch(function () {
          submitButtons.forEach(function (button) {
            button.classList.remove("loading");
            button.disabled = false;
          });
          showCartToast({ error: true, message: "Something went wrong adding this to your cart." });
        });
    },
    true
  );
})();
